import { CALCULATED, FieldTooltipContent } from './field-tooltip-content';

export interface BuildHeaderTooltipParams {
  /** The column's display name (fd?.displayName ?? cm.displayName). */
  displayName?: string;
  /** Existing description from FieldDefinition/ColumnMeta (fd?.description ?? cm.description). */
  fallbackDescription?: string;
  /** Code-defined content for this field, if any. */
  content?: FieldTooltipContent;
}

/**
 * Composes the multi-line `headerTooltip` string for a Portfolio Overview
 * column header.
 *
 * Lines are emitted only for the parts that exist, in this order:
 *   1. field name (displayName)
 *   2. description   (registry description wins; else the fd/cm fallback)
 *   3. `Source: <source>`  — suppressed when the source is the uniform
 *      "Calculated" (low-signal on every metric header); meaningful,
 *      distinguishing sources (e.g. General Ledger / User Input) still render.
 *   4. `Formula: <formula>`
 *
 * Returns `undefined` when there is no description, source, or formula — the
 * field name alone is not a "definition", so ag-grid omits the tooltip
 * entirely (AC #4: no tooltip content where no definition has been provided).
 *
 * A description that merely repeats the column name is treated as *no*
 * description: upstream (`filterOutUnwantedColumns`) defaults every column's
 * `description` to its `displayName`, so without this a column with no real
 * definition would both duplicate its name and (wrongly) show a tooltip.
 */
export function buildHeaderTooltip({
  displayName,
  fallbackDescription,
  content,
}: BuildHeaderTooltipParams): string | undefined {
  const name = trimToUndefined(displayName);
  let description = trimToUndefined(content?.description ?? fallbackDescription);
  if (description && description === name) {
    description = undefined;
  }
  // The uniform "Calculated" source is low-signal on every metric header, so
  // it is not rendered; distinguishing sources (GL / Aumni / User Input, etc.)
  // still show. The value stays in the registry as documentation.
  const rawSource = trimToUndefined(content?.source);
  const source = rawSource === CALCULATED ? undefined : rawSource;
  const formula = trimToUndefined(content?.formula);

  // No definition of any kind → no tooltip.
  if (!description && !source && !formula) {
    return undefined;
  }

  const lines: string[] = [];
  if (name) {
    lines.push(name);
  }
  if (description) {
    lines.push(description);
  }
  if (source) {
    lines.push(`Source: ${source}`);
  }
  if (formula) {
    lines.push(`Formula: ${formula}`);
  }

  return lines.join('\n');
}

function trimToUndefined(value: string | undefined): string | undefined {
  if (value == null) {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}
